// Loads monster-pack spritesheets on demand and composites (anim, dir, frame)
// views into cached canvases. Mirrors paperdollRenderer's cache + notify
// pattern so Tokens can re-render once images finish loading.

import { MONSTER_PACKS, FRAME_SIZE } from './monsterPacks';

// Admurin's pack uses 4 directional rows: down, left, right, up.
// Source grid: row 0 = down, 1 = left, 2 = right, 3 = up.
const DIR_ROW = { down: 0, left: 1, right: 2, up: 3 };

const imageCache = new Map(); // url -> { img, ready, errored }
const frameCache = new Map(); // key -> HTMLCanvasElement
const FRAME_CACHE_CAP = 256;
const listeners = new Set();

function notify() { listeners.forEach((cb) => cb()); }

function getImage(url) {
  let entry = imageCache.get(url);
  if (entry) return entry;
  const img = new Image();
  entry = { img, ready: false };
  imageCache.set(url, entry);
  img.onload = () => { entry.ready = true; notify(); };
  img.onerror = () => { entry.errored = true; };
  img.src = url;
  return entry;
}

function sheetUrl(packKey, anim) {
  return `/monster-sprites/${packKey}/${anim}.png`;
}

function resolveAnim(pack, anim) {
  // anim is 'idle' | 'move' | 'attack'. Fall back to idle if move/attack missing.
  if (pack.frames && pack.frames[anim]) return anim;
  return 'idle';
}

/**
 * Return a composited HTMLCanvasElement showing (packKey, anim, dir, frame)
 * scaled to `size`px. Returns null until the underlying sheet loads.
 * Subscribers fire when images complete so callers can re-render.
 */
export function getMonsterPackFrame(packKey, anim, dir, frame, size) {
  if (!packKey) return null;
  const pack = MONSTER_PACKS[packKey];
  if (!pack) return null;
  if (typeof document === 'undefined') return null;

  const actualAnim = resolveAnim(pack, anim || 'idle');

  // Read geometry from per-anim data when available (new manifest), otherwise
  // fall back to pack-level fields (legacy manifests).
  const geom = pack.anims?.[actualAnim] || {
    rows: pack.rows || 4,
    frameSize: pack.frameSize || FRAME_SIZE,
    frames: pack.frames[actualAnim] || 1,
  };

  // Wrap frame index with modulo so packs with fewer frames than the animator's
  // cycle (e.g. 2-frame idle vs animator's 4-tick loop) still animate smoothly
  // instead of clamping and freezing on the last frame.
  const f = geom.frames > 0 ? ((frame | 0) % geom.frames + geom.frames) % geom.frames : 0;
  // Clamp direction row to the sheet's actual row count so we don't read past
  // the bottom into empty pixels on sheets with fewer than 4 directional rows.
  const row = Math.min(geom.rows - 1, DIR_ROW[dir] ?? 0);

  const key = `${packKey}|${actualAnim}|${row}|${f}|${size}`;
  const hit = frameCache.get(key);
  if (hit) {
    frameCache.delete(key);
    frameCache.set(key, hit);
    return hit;
  }

  const url = sheetUrl(packKey, actualAnim);
  const entry = getImage(url);
  if (entry.errored) return null;
  if (!entry.ready) return null;

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = false;

  const fs = geom.frameSize;
  const sx = f * fs;
  const sy = row * fs;
  ctx.drawImage(entry.img, sx, sy, fs, fs, 0, 0, size, size);

  if (frameCache.size >= FRAME_CACHE_CAP) {
    const oldest = frameCache.keys().next().value;
    if (oldest !== undefined) frameCache.delete(oldest);
  }
  frameCache.set(key, canvas);
  return canvas;
}

export function preloadMonsterPack(packKey) {
  if (!packKey || !MONSTER_PACKS[packKey]) return;
  getImage(sheetUrl(packKey, 'idle'));
  if (MONSTER_PACKS[packKey].frames.move) getImage(sheetUrl(packKey, 'move'));
}

export function subscribe(cb) { listeners.add(cb); return () => listeners.delete(cb); }
